import { enumMembers, type ApiSchema } from "../apiSchema";
import { ROUTE_UNAVAILABILITIES, type RouteUnavailability } from "../connectorStatus";
import { jsonBody, requestJson, withQuery } from "../http/httpClient";
import {
  readArray,
  JsonContractError,
  readBoolean,
  readNullableInteger,
  readNullableString,
  readObject,
  readOneOf,
  readString,
  type JsonObject,
} from "../http/jsonDecoding";
import type { AgentRoute, ApiKeyRoute } from "./liveMessages";

/**
 * Which routes of a connector can run a Thread's turns before it goes live (Q-63; route
 * `GET /threads/{t}/route-options`). The server resolves every capability of a route from
 * the connector's facts and the project's reviewed overrides, keeping the source of each
 * value (capabilities.md CAP-3), and checks each turn of the Thread's default route by the
 * same rule as when it prepares a turn (T-4). Unknown counts as a gap.
 */

export type CapabilitySource = ApiSchema<"CapabilitySource">;
export type RequirementOutcome = ApiSchema<"RequirementOutcome">;
export type CapabilityValue = boolean | number | string;

const CAPABILITY_SOURCES = enumMembers<CapabilitySource>({
  override_project: true,
  override_installation: true,
  override_host: true,
  reported: true,
  probed: true,
  observed: true,
});
const OUTCOMES = enumMembers<RequirementOutcome>({ met: true, not_met: true, unknown: true });
const GAP_KNOWLEDGE = enumMembers<"no" | "unknown">({ no: true, unknown: true });
const ROUTE_KINDS = enumMembers<(AgentRoute | ApiKeyRoute)["kind"]>({ agent: true, api_key: true });

/** One source's say about a capability. */
export interface CapabilityClaim {
  readonly value: CapabilityValue;
  readonly source: CapabilitySource;
  readonly observedAt: string | null;
  readonly evidence: string;
  /** The `capability_overrides` item version, for a project override. */
  readonly override: string | null;
}

export interface ResolvedCapability {
  readonly key: string;
  /** `null` for a key the provider reported that is not in the vocabulary. */
  readonly description: string | null;
  /** `null` when overrides of one source disagree: then it is unknown. */
  readonly decided: CapabilityClaim | null;
  readonly disagreeing: readonly CapabilityClaim[];
  /** When nothing stated decides: an estimate an agent reported, shown and never matched (D17). */
  readonly hint: CapabilityClaim | null;
}

export interface RequirementBasis {
  readonly capability: string;
  readonly equals: CapabilityValue | null;
  readonly atLeast: number | null;
  readonly outcome: RequirementOutcome;
  readonly matchedKey: string | null;
  readonly source: CapabilitySource | null;
  readonly evidence: string | null;
  /** For an unknown requirement, the estimate an agent reported, if any. */
  readonly hint: CapabilityValue | null;
}

export interface CapabilityGap {
  readonly capability: string;
  readonly known: "no" | "unknown";
}

export interface TurnFit {
  /** `null` stands for reply turns. */
  readonly stepKey: string | null;
  readonly requirements: readonly RequirementBasis[];
  readonly gaps: readonly CapabilityGap[];
}

export interface RouteOption {
  readonly route: AgentRoute | ApiKeyRoute;
  /** The agent or API route is ready and offers the model. */
  readonly available: boolean;
  /** Module C's reason it can't run now (CP-11); `null` when it can. */
  readonly unavailableReason: RouteUnavailability | null;
  /** The agent's own name or the API's host: what an override names the route by. */
  readonly routeIdentity: string | null;
  readonly capabilities: readonly ResolvedCapability[];
  readonly fits: readonly TurnFit[];
  readonly runsEveryTurn: boolean;
  /** The route has no tools and the reply tools are only the defaults: they come off it, and `fits` match the replies without them (the server decides). */
  readonly dropsDefaultReplyTools: boolean;
}

export interface RouteOptionListing {
  readonly reportReceivedAt: string | null;
  /** The project's override item versions the options consulted. */
  readonly overridesUsed: readonly string[];
  readonly options: readonly RouteOption[];
  /** Steps that ask the host for a file in their workspace (Q-81): a profile must open it to tools. */
  readonly workspaceFileSteps: readonly string[];
  /** The permission profile to preselect on this connector, as the server suggests it (Q-81). */
  readonly suggestedPermissionProfile: string;
  /** An agent step is left to run: without one the Thread goes live with no route, its tool and person steps running without an LLM. */
  readonly needsRoute: boolean;
}

/** As the Thread's default route; with `stepKey`, as that agent step's own route, matched with its requirements alone (F106). */
export function listRouteOptions(threadId: string, connectorId: string, signal?: AbortSignal, stepKey: string | null = null): Promise<RouteOptionListing> {
  return requestJson({
    method: "GET",
    path: withQuery(`/threads/${encodeURIComponent(threadId)}/route-options`, stepKey === null ? { connector_id: connectorId } : { connector_id: connectorId, step_key: stepKey }),
    signal,
    decodeResponse: decodeListing,
  });
}

/** What a start would run with: its Workflow, the content version it binds and the routes chosen so far. */
export interface StartRouteQuestion {
  readonly projectId: string;
  readonly workflow: string;
  readonly contentVersion: string;
  /** The routes chosen so far: steps with their own leave the default's fit. */
  readonly routes: { readonly route: AgentRoute | ApiKeyRoute; readonly step_routes: Readonly<Record<string, AgentRoute | ApiKeyRoute>> } | null;
}

/**
 * The same before a Thread exists (F106, TR-A2-04): for a start of the Workflow, so its LLMs are
 * chosen before it runs. With no PC: no routes, only whether the start needs an LLM (`needsRoute`).
 */
export function listStartRouteOptions(question: StartRouteQuestion, connectorId: string | null, signal?: AbortSignal, stepKey: string | null = null): Promise<RouteOptionListing> {
  const body = {
    workflow: question.workflow,
    content_version: question.contentVersion,
    connector_id: connectorId,
    step_key: stepKey,
    routes: question.routes === null ? null : { route: question.routes.route, step_routes: { ...question.routes.step_routes } },
  } satisfies ApiSchema<"StartRouteOptionsBody">;
  return requestJson({
    method: "POST",
    path: `/projects/${encodeURIComponent(question.projectId)}/route-options`,
    body: jsonBody(body),
    signal,
    decodeResponse: decodeListing,
  });
}

function decodeListing(body: unknown): RouteOptionListing {
  const listing = readObject(body, "route options");
  return {
    reportReceivedAt: readNullableString(listing, "report_received_at"),
    overridesUsed: readArray(listing["overrides_used"], "overrides used", (item) => readString({ item }, "item")),
    options: readArray(listing["options"], "options", (item) => decodeRouteOption(readObject(item, "route option"))),
    workspaceFileSteps: readArray(listing["workspace_file_steps"], "workspace file steps", (item) => readString({ item }, "item")),
    suggestedPermissionProfile: readString(listing, "suggested_permission_profile"),
    needsRoute: readBoolean(listing, "needs_route"),
  };
}

function decodeRouteOption(option: JsonObject): RouteOption {
  return {
    route: decodeRoute(readObject(option["route"], "route")),
    available: readBoolean(option, "available"),
    unavailableReason: option["unavailable_reason"] == null ? null : readOneOf(option, "unavailable_reason", ROUTE_UNAVAILABILITIES),
    routeIdentity: readNullableString(option, "route_identity"),
    capabilities: readArray(option["capabilities"], "capabilities", (item) => decodeResolved(readObject(item, "capability"))),
    fits: readArray(option["fits"], "fits", (item) => {
      const fit = readObject(item, "fit");
      return {
        stepKey: readNullableString(fit, "step_key"),
        requirements: readArray(fit["requirements"], "requirements", (entry) => decodeRequirement(readObject(entry, "requirement"))),
        gaps: readArray(fit["gaps"], "gaps", (gapItem) => {
          const gap = readObject(gapItem, "gap");
          return { capability: readString(gap, "capability"), known: readOneOf(gap, "known", GAP_KNOWLEDGE) };
        }),
      };
    }),
    runsEveryTurn: readBoolean(option, "runs_every_turn"),
    dropsDefaultReplyTools: readBoolean(option, "drops_default_reply_tools"),
  };
}

function decodeResolved(resolved: JsonObject): ResolvedCapability {
  const decided = resolved["decided"];
  return {
    key: readString(resolved, "key"),
    description: readNullableString(resolved, "description"),
    decided: decided === null || decided === undefined ? null : decodeClaim(readObject(decided, "claim")),
    disagreeing: readArray(resolved["disagreeing"], "disagreeing", (item) => decodeClaim(readObject(item, "claim"))),
    hint: resolved["hint"] === null || resolved["hint"] === undefined ? null : decodeClaim(readObject(resolved["hint"], "hint")),
  };
}

function decodeClaim(claim: JsonObject): CapabilityClaim {
  return {
    value: readValue(claim, "value"),
    source: readOneOf(claim, "source", CAPABILITY_SOURCES),
    observedAt: readNullableString(claim, "observed_at"),
    evidence: readString(claim, "evidence"),
    override: readNullableString(claim, "override"),
  };
}

function decodeRequirement(requirement: JsonObject): RequirementBasis {
  const equals = requirement["equals"];
  const source = requirement["source"];
  return {
    capability: readString(requirement, "capability"),
    equals: equals === null || equals === undefined ? null : readValue(requirement, "equals"),
    atLeast: readNullableInteger(requirement, "at_least"),
    outcome: readOneOf(requirement, "outcome", OUTCOMES),
    matchedKey: readNullableString(requirement, "matched_key"),
    source: source === null || source === undefined ? null : readOneOf(requirement, "source", CAPABILITY_SOURCES),
    evidence: readNullableString(requirement, "evidence"),
    hint: requirement["hint"] === null || requirement["hint"] === undefined ? null : readValue(readObject(requirement["hint"], "hint"), "value"),
  };
}

function readValue(object: JsonObject, member: string): CapabilityValue {
  const value = object[member];
  if (typeof value === "boolean" || typeof value === "number" || typeof value === "string") {
    return value;
  }
  throw new JsonContractError(`Expected "${member}" to be a boolean, a number or a string.`);
}

/** A turn route as the server spells it; also how a remembered route is read back (`automaticHosting`). */
export function decodeRoute(route: JsonObject): AgentRoute | ApiKeyRoute {
  if (readOneOf(route, "kind", ROUTE_KINDS) === "agent") {
    return { kind: "agent", agent_id: readString(route, "agent_id"), model_id: readNullableString(route, "model_id") };
  }
  return { kind: "api_key", route_id: readString(route, "route_id"), model_id: readString(route, "model_id") };
}
